/**
 * Application store (no external state library): a single immutable state object, listeners,
 * and a React hook based on useSyncExternalStore. Design edits go through `commitDesign`,
 * which records undo history and schedules stitch regeneration in the worker.
 */
import { useSyncExternalStore } from 'react';
import type { AccountUser } from '../account/api';
import { type Lang, getLang } from '../i18n/i18n';
import { createDesign } from '../engine/model/design';
import type { Design, EmbObject } from '../engine/model/types';
import type { DecodedImage } from '../imaging/decode';
import { StitchClient } from '../workers/client';
import type { GenerateResult } from '../workers/protocol';
import { withPreferredMachine } from './machinePref';
import { type HistoryState, canRedo, canUndo, commit, createHistory, redo, undo } from './history';

export type Tool = 'select' | 'node' | 'rect' | 'ellipse' | 'polygon' | 'line' | 'column' | 'pan' | 'measure';
export type ViewMode = 'edit' | 'simulate';
export type Panel = 'import' | 'export' | 'projects' | 'patch' | 'new' | 'account' | 'admin' | null;

export interface Layers {
  image: boolean;
  shapes: boolean;
  stitches: boolean;
  jumps: boolean;
  hoop: boolean;
  grid: boolean;
}

export interface Viewport {
  /** Screen pixels per mm. */
  zoom: number;
  panX: number;
  panY: number;
}

export interface ProjectMeta {
  id: string | null;
  name: string;
  createdAt: string | null;
  dirty: boolean;
  lastSaved: string | null;
  /** A copy exists in the cloud (logged-in users always save there). */
  cloud: boolean;
  /** Folder in the project list (null = none). */
  folder: string | null;
}

export interface ImageState {
  decoded: DecodedImage;
  /** Object URL for display only; recreated from bytes whenever a project is loaded. */
  url: string;
}

export interface NodeRef {
  objectId: string;
  /** 'outer', 'hole:N', 'nodes', 'railA', 'railB' */
  path: string;
  index: number;
}

export interface Toast {
  id: number;
  kind: 'info' | 'error' | 'success';
  message: string;
}

export interface AppState {
  history: HistoryState<Design>;
  selection: string[];
  selectedNode: NodeRef | null;
  tool: Tool;
  mode: ViewMode;
  layers: Layers;
  viewport: Viewport;
  project: ProjectMeta;
  image: ImageState | null;
  stitch: { result: GenerateResult | null; pending: boolean; error: string | null; forDesign: Design | null };
  clipboard: EmbObject[];
  panel: Panel;
  toasts: Toast[];
  /** Object highlighted from the simulator or layer hover. */
  highlight: string | null;
  /** Canvas click picks a point for the selected object / machine instead of editing. */
  pick: 'entry' | 'exit' | 'hoopCenter' | null;
  /** Narrow screens: which side drawer (phones: bottom sheet) is open. */
  drawer: 'layers' | 'inspector' | 'menu' | null;
  /** Incremented to ask the canvas to zoom to fit the design. */
  fitNonce: number;
  /** Incremented to zoom so the whole hoop is visible (after choosing a machine or hoop). */
  hoopFitNonce: number;
  /** UI language (re-renders the app when changed). */
  lang: Lang;
  /** The start card on an empty canvas was closed (shown again for a new project). */
  startCardClosed: boolean;
  /** Accounts are available when the app is served by the StitchCraft server with a database. */
  account: { enabled: boolean; user: AccountUser | null };
}

export function initialState(design: Design = withPreferredMachine(createDesign())): AppState {
  return {
    history: createHistory(design),
    selection: [],
    selectedNode: null,
    tool: 'select',
    mode: 'edit',
    layers: { image: true, shapes: true, stitches: true, jumps: true, hoop: true, grid: true },
    viewport: { zoom: 4, panX: 40, panY: 40 },
    project: { id: null, name: design.name, createdAt: null, dirty: false, lastSaved: null, cloud: false, folder: null },
    image: null,
    stitch: { result: null, pending: false, error: null, forDesign: null },
    clipboard: [],
    panel: null,
    toasts: [],
    highlight: null,
    pick: null,
    drawer: null,
    fitNonce: 0,
    hoopFitNonce: 0,
    lang: getLang(),
    account: { enabled: false, user: null },
    startCardClosed: false,
  };
}

type Listener = () => void;

export class Store {
  private state: AppState;
  private listeners = new Set<Listener>();
  private client: StitchClient | null = null;
  private regenTimer: ReturnType<typeof setTimeout> | null = null;
  private toastId = 0;
  regenerateDelay = 120;

  constructor(state: AppState = initialState(), private autoGenerate = true) {
    this.state = state;
  }

  getState = (): AppState => this.state;

  subscribe = (l: Listener): (() => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  set(patch: Partial<AppState> | ((s: AppState) => Partial<AppState>)): void {
    const p = typeof patch === 'function' ? patch(this.state) : patch;
    const prevDesign = this.state.history.present;
    this.state = { ...this.state, ...p };
    // keep selection valid after undo/redo/deletes
    const design = this.state.history.present;
    if (design !== prevDesign) {
      const ids = new Set(design.objects.map((o) => o.id));
      if (this.state.selection.some((id) => !ids.has(id))) this.state = { ...this.state, selection: this.state.selection.filter((id) => ids.has(id)) };
      if (this.state.selectedNode && !ids.has(this.state.selectedNode.objectId)) this.state = { ...this.state, selectedNode: null };
      this.scheduleRegenerate();
    }
    for (const l of this.listeners) l();
  }

  get design(): Design {
    return this.state.history.present;
  }

  /** Records an undoable design change. Same coalesceKey within a short time = one undo step. */
  commitDesign(next: Design, coalesceKey: string | null = null): void {
    if (next === this.design) return;
    this.set((s) => ({ history: commit(s.history, next, coalesceKey), project: { ...s.project, dirty: true } }));
  }

  /** Replaces the design without undo (loading a project or a new document). */
  resetDesign(design: Design, project: Partial<ProjectMeta> = {}): void {
    this.set((s) => ({
      history: createHistory(design),
      selection: [],
      selectedNode: null,
      project: { ...s.project, dirty: false, cloud: false, folder: null, name: design.name, ...project },
      stitch: { result: null, pending: true, error: null, forDesign: null },
    }));
  }

  undo(): void {
    if (canUndo(this.state.history)) this.set((s) => ({ history: undo(s.history), project: { ...s.project, dirty: true } }));
  }

  redo(): void {
    if (canRedo(this.state.history)) this.set((s) => ({ history: redo(s.history), project: { ...s.project, dirty: true } }));
  }

  toast(kind: Toast['kind'], message: string, ms = 5000): void {
    const id = ++this.toastId;
    this.set((s) => ({ toasts: [...s.toasts.slice(-3), { id, kind, message }] }));
    if (ms > 0) setTimeout(() => this.dismissToast(id), ms);
  }

  dismissToast(id: number): void {
    this.set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  }

  getClient(): StitchClient {
    if (!this.client) this.client = new StitchClient();
    return this.client;
  }

  private scheduleRegenerate(): void {
    if (!this.autoGenerate) return;
    if (this.regenTimer) clearTimeout(this.regenTimer);
    if (!this.state.stitch.pending) this.state = { ...this.state, stitch: { ...this.state.stitch, pending: true } };
    this.regenTimer = setTimeout(() => void this.regenerate(), this.regenerateDelay);
  }

  /** Regenerates stitches for the current design; stale results are discarded. */
  async regenerate(): Promise<void> {
    const design = this.design;
    try {
      const result = await this.getClient().generate(design);
      if (!result || this.design !== design) return;
      this.set({ stitch: { result, pending: false, error: null, forDesign: design } });
    } catch (e) {
      if (this.design !== design) return;
      this.set((s) => ({ stitch: { ...s.stitch, pending: false, error: e instanceof Error ? e.message : String(e) } }));
    }
  }

  /** Forces an immediate regeneration (e.g. after loading). */
  regenerateNow(): Promise<void> {
    if (this.regenTimer) clearTimeout(this.regenTimer);
    return this.regenerate();
  }
}

export const store = new Store();

export function useApp<T>(selector: (s: AppState) => T, s: Store = store): T {
  return useSyncExternalStore(s.subscribe, () => selector(s.getState()), () => selector(s.getState()));
}

export const useDesign = (): Design => useApp((s) => s.history.present);
